/**
 * Utilitários para processamento e transformação de dados do Flow
 */

// Valores padrão para novo fluxo
export const getDefaultFlowValues = () => ({
  name: "",
  nameTranslations: {},
  requirementScope: "",
  documents: [
    {
      fileName: "",
      fileNameTranslations: {},
      allowedFileTypes: [],
      productsEcommerce: [],
      mandatory: false,
      insuranceDashboard: false,
    },
  ],
  allowClient: true,
  allowServiceProvider: false,
  allowedClientTypes: [],
  allowedServiceTypes: [],
  allowedActorTypes: [],
  boothRequirement: null,
  contractProducts: [],
  documentPackage: false,
  active: true,
});

// Função para limpar dados do formulário
export const cleanFormData = (data) => {
  const cleanedData = { ...data };

  if (!cleanedData.allowClient) {
    cleanedData.allowedClientTypes = [];
  }
  if (!cleanedData.allowServiceProvider) {
    cleanedData.allowedServiceTypes = [];
  }

  if (!Array.isArray(cleanedData.allowedActorTypes)) {
    cleanedData.allowedActorTypes = [];
  }

  if (!cleanedData.boothRequirement) {
    cleanedData.boothRequirement = "BOTH";
  }

  return cleanedData;
};

const mapOptionsToValues = (options) =>
  Array.isArray(options)
    ? options
        .map((option) => {
          if (typeof option === "string") return option;
          if (option && typeof option.value === "string") return option.value;
          return "";
        })
        .filter((v) => !!v)
    : [];

// Função para transformar dados do formulário para o formato da API
export const transformFormDataToApi = (
  cleanedData,
  id,
  action,
  initialData
) => {
  const isDocumentPackage = !!cleanedData.documentPackage;

  return {
    name: cleanedData.name,
    nameTranslations: cleanedData.nameTranslations || {},
    requirementScope: cleanedData.requirementScope,
    active: true,
    // Quando documentPackage = true, o backend recebe o array como
    // DocumentExecutionRequest[] com todos os documentos obrigatórios.
    documents: cleanedData.documents.map((file, index) => ({
      ...(id && action === "edit" && initialData?.documents?.[index]?.id
        ? { id: initialData.documents[index].id }
        : {}),
      fileName: file.fileName,
      fileNameTranslations: file.fileNameTranslations || {},
      allowedFileTypes: mapOptionsToValues(file.allowedFileTypes),
      products: Array.isArray(file.productsEcommerce)
        ? file.productsEcommerce
            .map((option) =>
              typeof option === "object" && option.label && option.value
                ? { productName: option.label, code: option.value }
                : null
            )
            .filter((v) => v !== null && v !== undefined)
        : [],
      // Em pacote de documentos, todos os arquivos são obrigatórios
      mandatory: isDocumentPackage ? true : file.mandatory || false,
      insuranceDashboard: file.insuranceDashboard || false,
    })),
    allowClient: !!cleanedData.allowClient,
    allowServiceProvider: !!cleanedData.allowServiceProvider,
    allowedClientTypes:
      cleanedData.allowClient &&
      Array.isArray(cleanedData.allowedClientTypes) &&
      cleanedData.allowedClientTypes.length > 0
        ? mapOptionsToValues(cleanedData.allowedClientTypes)
        : [],
    allowedServiceTypes:
      cleanedData.allowServiceProvider &&
      Array.isArray(cleanedData.allowedServiceTypes) &&
      cleanedData.allowedServiceTypes.length > 0
        ? mapOptionsToValues(cleanedData.allowedServiceTypes)
        : [],
    allowedActorTypes:
      Array.isArray(cleanedData.allowedActorTypes) &&
      cleanedData.allowedActorTypes.length > 0
        ? mapOptionsToValues(cleanedData.allowedActorTypes)
        : [],
    boothRequirement:
      typeof cleanedData.boothRequirement === "string"
        ? cleanedData.boothRequirement
        : cleanedData.boothRequirement?.value || "BOTH",
    documentPackage: isDocumentPackage,
    // ...existing code...
    ...(Array.isArray(cleanedData.contractProducts) &&
    cleanedData.contractProducts.length > 0
      ? {
          contractProducts: cleanedData.contractProducts.map((product) => {
            if (typeof product === "string") {
              return { productCode: product, productName: product };
            }
            if (product.value && product.label) {
              const nameFromLabel = product.label.includes(" - ")
                ? product.label.split(" - ").slice(1).join(" - ")
                : product.value;
              return {
                productCode: product.value,
                productName: nameFromLabel || product.value,
              };
            }
            return {
              productCode: product.productCode || product.value,
              productName:
                product.name ||
                product.productName ||
                product.productCode ||
                product.value,
            };
          }),
        }
      : {}),
  };
};

// Função para processar dados do flow para edição
export const processFlowDataForEdit = (
  flow,
  transformDocuments,
  mappedAllowedClientTypes,
  mappedAllowedServiceTypes,
  actorTypesLocalized,
  boothRequirementTypesLocalized
) => {
  const defaultAllOption = actorTypesLocalized?.find(
    (at) => at.value === "ALL"
  ) || { value: "ALL", label: "ALL" };
  const mappedAllowedActorTypes = Array.isArray(flow.allowedActorTypes)
    ? flow.allowedActorTypes.map((actorType) => {
        const value =
          typeof actorType === "string" ? actorType : actorType.value;
        const match = actorTypesLocalized?.find((at) => at.value === value);
        return match || { value, label: value };
      })
    : [defaultAllOption];

  const defaultBoothOption = boothRequirementTypesLocalized?.find(
    (br) => br.value === "BOTH"
  ) || { value: "BOTH", label: "BOTH" };
  const mappedBoothRequirement = flow.boothRequirement
    ? boothRequirementTypesLocalized?.find(
        (br) => br.value === flow.boothRequirement
      ) || { value: flow.boothRequirement, label: flow.boothRequirement }
    : defaultBoothOption;

  return {
    ...flow,
    documents: transformDocuments(flow.documents),
    requirementScope: flow.requirementScope || "",
    allowClient: !!flow.allowClient,
    allowServiceProvider: !!flow.allowServiceProvider,
    allowedClientTypes: mappedAllowedClientTypes,
    allowedServiceTypes: mappedAllowedServiceTypes,
    allowedActorTypes: mappedAllowedActorTypes,
    boothRequirement: mappedBoothRequirement,
    nameTranslations: flow.nameTranslations || {},
    documentPackage: flow.documentPackage || false,
    // Mapear contractProducts para formato do react-select { value, label }
    contractProducts: Array.isArray(flow.contractProducts)
      ? flow.contractProducts.map((p) => ({
          value: p.productCode,
          label: `${p.productCode} - ${
            p.productName || p.name || p.productCode
          }`,
        }))
      : [],
  };
};

export const DELETE_SUCCESS_CODES = [200, 201, 45002, 45006];
